import { useEffect, useState } from "preact/hooks"

import { Button } from "../../../components/ui/button"
import { Checkbox } from "../../../components/ui/checkbox"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../../components/ui/dialog"
import { Input } from "../../../components/ui/input"
import { Label } from "../../../components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select"
import { toast } from "../../../components/ui/use-toast"
import { exactCurrencyFormatter } from "../../../utils"
import * as ForecastIO from "../api/forecast_io_api"
import type { ForecastVersion } from "../types"
import { PlannedCellsTable } from "./planned_cells_table"

interface SeedFromActualsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The version being seeded (the 2027 budget). */
  target: ForecastVersion | null
  /** Every version the brand can copy actuals from (the target itself included). */
  versions: ForecastVersion[]
  on_applied: () => void | Promise<void>
}

/** The year the version's actuals most likely live in: the year before its fiscal year. */
export function default_seed_years(target: ForecastVersion | null, now: Date): { from_year: number; to_year: number } {
  const to_year = target?.fiscal_year ?? now.getFullYear() + 1
  return { from_year: to_year - 1, to_year }
}

/**
 * Item 22: "copy last year's actual sales by buyer into a base budget, then add or remove from
 * there." Copies one year's actual rows from a version into the next year on this one, leaf
 * for leaf. Previews first; existing forecast cells are skipped unless overwrite is ticked.
 */
export function SeedFromActualsDialog(props: SeedFromActualsDialogProps) {
  const { open, onOpenChange, target, versions, on_applied } = props
  const defaults = default_seed_years(target, new Date())
  const [source_id, set_source_id] = useState<string>(target?.id ?? "")
  const [from_year, set_from_year] = useState<number>(defaults.from_year)
  const [to_year, set_to_year] = useState<number>(defaults.to_year)
  const [overwrite, set_overwrite] = useState(false)
  const [preview, set_preview] = useState<ForecastIO.SeedPreviewResponse | null>(null)
  const [busy, set_busy] = useState<"preview" | "apply" | null>(null)
  const [error, set_error] = useState<string | null>(null)

  // Reopening for another version resets the form to that version's years.
  useEffect(() => {
    if (!open) return
    const next = default_seed_years(target, new Date())
    set_source_id(target?.id ?? "")
    set_from_year(next.from_year)
    set_to_year(next.to_year)
    set_overwrite(false)
    set_preview(null)
    set_error(null)
  }, [open, target?.id])

  const candidates = versions.filter((v: ForecastVersion) => !v.archived_at && (v.period_grain ?? "month") === (target?.period_grain ?? "month"))
  const is_week = (target?.period_grain ?? "month") === "week"

  async function run(dry_run: boolean): Promise<void> {
    if (!target) return
    set_busy(dry_run ? "preview" : "apply")
    set_error(null)
    try {
      const result = await ForecastIO.seed_from_actuals(target.id, {
        source_version_id: source_id && source_id !== target.id ? source_id : null,
        from_year,
        to_year,
        overwrite,
        dry_run,
      })
      set_preview(result)
      if (result.applied) {
        toast({
          title: `${to_year} seeded from ${from_year} actuals`,
          description: `${result.cells.length.toLocaleString()} forecast row${result.cells.length === 1 ? "" : "s"} written to ${target.name} (${exactCurrencyFormatter(
            Number(result.total_dollars)
          )}).`,
        })
        onOpenChange(false)
        await on_applied()
      }
    } catch (err) {
      set_error(err instanceof Error ? err.message : String(err))
    } finally {
      set_busy(null)
    }
  }

  const can_apply = preview != null && !preview.applied && preview.cells.length > 0 && busy == null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>Start from last year's actuals</DialogTitle>
          <DialogDescription>
            Copy one year's actual sales, customer by customer and SKU by SKU, into the next year of{" "}
            <strong>{target?.name ?? "this version"}</strong> as forecast rows — a base budget to add to or trim. Actuals come
            from the version you pick (sync them there first if it has none).
          </DialogDescription>
        </DialogHeader>

        {is_week ? (
          <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            This is a weekly plan. ISO weeks do not line up from one year to the next, so a year cannot be copied forward
            here; use a monthly or 4-week version.
          </p>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-4">
          <div className="space-y-1.5 sm:col-span-2">
            <Label>Actuals from</Label>
            <Select value={source_id} onValueChange={(value: string) => { set_source_id(value); set_preview(null) }}>
              <SelectTrigger className="h-9">
                <SelectValue placeholder="Pick a version" />
              </SelectTrigger>
              <SelectContent>
                {candidates.map((v: ForecastVersion) => (
                  <SelectItem key={v.id} value={v.id}>
                    {v.name}
                    {v.id === target?.id ? " (this version)" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Actuals year</Label>
            <Input type="number" value={from_year} onInput={(e: Event) => { set_from_year(Number((e.currentTarget as HTMLInputElement).value)); set_preview(null) }} />
          </div>
          <div className="space-y-1.5">
            <Label>Budget year</Label>
            <Input type="number" value={to_year} onInput={(e: Event) => { set_to_year(Number((e.currentTarget as HTMLInputElement).value)); set_preview(null) }} />
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <Checkbox checked={overwrite} onCheckedChange={(checked: boolean) => { set_overwrite(checked === true); set_preview(null) }} />
          Replace forecast cells this version already has for the same SKU and period
        </label>

        {error ? <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p> : null}

        {preview ? (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-4 text-sm">
              <Stat label={`${preview.from_year} actual rows`} value={preview.source_actual_rows.toLocaleString()} />
              <Stat label="Rows to add" value={preview.rows_to_create.toLocaleString()} />
              <Stat label="Rows to replace" value={preview.rows_to_replace.toLocaleString()} />
              <Stat label="Left as is" value={preview.rows_skipped_existing.toLocaleString()} />
              <Stat label="New tree rows" value={preview.new_node_count.toLocaleString()} />
              <Stat label={`${preview.to_year} total`} value={exactCurrencyFormatter(Number(preview.total_dollars))} />
            </div>
            {preview.cells.length > 0 ? (
              <PlannedCellsTable cells={preview.cells} />
            ) : (
              <p className="text-sm text-gray-600">
                Nothing to write: every {preview.to_year} cell these actuals map to already has a forecast. Tick replace to
                overwrite them.
              </p>
            )}
          </div>
        ) : null}

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" variant="outline" disabled={!target || is_week || busy != null || from_year === to_year} onClick={() => void run(true)}>
            {busy === "preview" ? "Previewing…" : "Preview"}
          </Button>
          <Button type="button" disabled={!can_apply} onClick={() => void run(false)}>
            {busy === "apply" ? "Writing…" : `Write ${preview?.cells.length ?? 0} row${preview?.cells.length === 1 ? "" : "s"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function Stat(props: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-gray-500">{props.label}</div>
      <div className="font-semibold tabular-nums text-gray-900">{props.value}</div>
    </div>
  )
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("default_seed_years", () => {
    const base = { id: "v", name: "x", created_at: "", updated_at: "", is_primary: false, data: [], periods: [], compare_mode: false, comparison_periods: [] }
    it("copies the year before the version's fiscal year into it", () => {
      expect(default_seed_years({ ...base, fiscal_year: 2027 } as ForecastVersion, new Date("2026-10-28"))).toEqual({ from_year: 2026, to_year: 2027 })
    })
    it("falls back to next year when the version has no fiscal year", () => {
      expect(default_seed_years({ ...base } as ForecastVersion, new Date("2026-10-28"))).toEqual({ from_year: 2026, to_year: 2027 })
      expect(default_seed_years(null, new Date("2026-02-01"))).toEqual({ from_year: 2026, to_year: 2027 })
    })
  })
}
